import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'

import {
  DELETE_REVIEW,
  FETCH_REVIEW_SUMMARY,
  FETCH_REVIEWS,
  FETCH_REVIEWS_ORDER,
  UPLOAD_REVIEW_PHOTO,
  UPSERT_REVIEW,
} from 'actions/apiActionConstants'

import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE,
  USER_REVIEW_SET_REVIEW_STAGE,
} from 'actions/actionConstants'
import { uniqueBy } from 'lib/array/arrayUtils'

export const initialReviewState: App.ReviewState = {
  reviews: {},
  reviewSummaries: {},
  reviewStage: 'initial',
  reviewFormSessionKey: undefined,
  isReviewDeleted: false,
  fetchingReviewCreate: false,
  reviewCreateError: undefined,
  reviewDeleteError: undefined,
  reviewDeletion: {},
  reviewsByOrder: {},
  reviewsByOrderLoading: {},
  reviewsByOrderError: {},
  reviewPhotosUpload: {},
}

function isCurrentReviewFormSession(
  state: App.ReviewState,
  action: { reviewFormSessionKey?: string },
): boolean {
  if (!state.reviewFormSessionKey) return true
  if (!action.reviewFormSessionKey) return false
  return action.reviewFormSessionKey === state.reviewFormSessionKey
}

const apiCalls = apiReducerSwitch<App.ReviewState>({
  [FETCH_REVIEWS]: {
    request: (state, action) => ({
      reviews: {
        ...state.reviews,
        [action.key]: {
          fetching: true,
          reviews: state.reviews[action.key]?.reviews ?? [],
          total: state.reviews[action.key]?.total ?? 0,
        },
      },
    }),
    success: (state, action) => ({
      reviews: {
        ...state.reviews,
        [action.key]: {
          fetching: false,
          reviews: uniqueBy([
            ...(state.reviews[action.key]?.reviews ?? []),
            ...action.data.reviews,
          ], review => review.position),
          total: action.data.total,
        },
      },
    }),
    failure: (state, action) => ({
      reviews: {
        ...state.reviews,
        [action.key]: {
          reviews: state.reviews[action.key]?.reviews ?? [],
          total: state.reviews[action.key]?.total ?? 0,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_REVIEWS_ORDER]: {
    request: (state, action) => ({
      isReviewDeleted: false,
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: true,
      },
      reviewsByOrderError: {
        ...state.reviewsByOrderError,
        [action.orderId]: false,
      },
    }),
    success: (state, action) => ({
      reviewsByOrder: {
        ...state.reviewsByOrder,
        [action.orderId]: action.data.reviews,
      },
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: false,
      },
      reviewsByOrderError: {
        ...state.reviewsByOrderError,
        [action.orderId]: false,
      },
    }),
    failure: (state, action) => ({
      reviewsByOrderLoading: {
        ...state.reviewsByOrderLoading,
        [action.orderId]: false,
      },
      reviewsByOrderError: {
        ...state.reviewsByOrderError,
        [action.orderId]: true,
      },
    }),
  },
  [UPSERT_REVIEW]: {
    request: (state, action) => {
      if (!isCurrentReviewFormSession(state, action)) return {}

      return {
        fetchingReviewCreate: true,
        reviewCreateError: undefined,
        isReviewDeleted: false,
      }
    },
    success: (state, action) => {
      const orderId = action.data.orderId
      const existingReviews = orderId ? state.reviewsByOrder[orderId] ?? [] : []
      const nextReviews = orderId
        ? existingReviews.some(review => review.id === action.data.id)
          ? existingReviews.map(review => review.id === action.data.id ? action.data : review)
          : [...existingReviews, action.data]
        : existingReviews
      const sessionMatches = isCurrentReviewFormSession(state, action)

      return {
        reviewsByOrder: orderId
          ? {
              ...state.reviewsByOrder,
              [orderId]: nextReviews,
            }
          : state.reviewsByOrder,
        ...(sessionMatches ? {
          reviewCreateError: undefined,
          fetchingReviewCreate: false,
          reviewStage: action.reviewStage ? action.reviewStage : state.reviewStage,
        } : {}),
      }
    },
    failure: (state, action) => {
      if (!isCurrentReviewFormSession(state, action)) return {}

      return {
        fetchingReviewCreate: false,
        reviewCreateError: action.error,
      }
    },
  },
  [DELETE_REVIEW]: {
    request: (state, action) => ({
      isReviewDeleted: false,
      reviewDeleteError: undefined,
      reviewDeletion: {
        ...state.reviewDeletion,
        [action.reviewId]: { loading: true, error: false },
      },
    }),
    success: (state, action) => {
      const existingReviews = state.reviewsByOrder[action.orderId]
      if (!existingReviews) {
        return {
          isReviewDeleted: true,
          reviewDeletion: {
            ...state.reviewDeletion,
            [action.reviewId]: { loading: false, error: false },
          },
        }
      }

      return {
        reviewsByOrder: {
          ...state.reviewsByOrder,
          [action.orderId]: existingReviews.filter(review => review.id !== action.reviewId),
        },
        isReviewDeleted: true,
        reviewDeletion: {
          ...state.reviewDeletion,
          [action.reviewId]: { loading: false, error: false },
        },
      }
    },
    failure: (state, action) => ({
      isReviewDeleted: false,
      reviewDeleteError: action.error,
      reviewDeletion: {
        ...state.reviewDeletion,
        [action.reviewId]: { loading: false, error: true },
      },
    }),
  },
  [UPLOAD_REVIEW_PHOTO]: {
    request: (state, action) => ({
      reviewPhotosUpload: {
        ...state.reviewPhotosUpload,
        [action.uploadId]: {
          isUploading: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      reviewPhotosUpload: {
        ...state.reviewPhotosUpload,
        [action.uploadId]: {
          ...state.reviewPhotosUpload[action.uploadId],
          isUploading: false,
          imageId: action.data.imageId,
        },
      },
    }),
    failure: (state, action) => {
      return ({
        reviewPhotosUpload: {
          ...state.reviewPhotosUpload,
          [action.uploadId]: {
            ...state.reviewPhotosUpload[action.uploadId],
            isUploading: false,
            error: action.error,
          },
        },
      })
    },
  },
  [FETCH_REVIEW_SUMMARY]: {
    request: (state, action) => ({
      reviewSummaries: {
        ...state.reviewSummaries,
        [action.key]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      reviewSummaries: {
        ...state.reviewSummaries,
        [action.key]: {
          summary: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      reviewSummaries: {
        ...state.reviewSummaries,
        [action.key]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
})

export default createAppReducer<App.ReviewState>(initialReviewState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE]: (state, action) => ({
    reviewPhotosUpload: {
      ...state.reviewPhotosUpload,
      [action.uploadId]: {
        ...state.reviewPhotosUpload[action.uploadId],
        percentage: action.percentage,
      },
    },
  }),
  [USER_REVIEW_SET_REVIEW_STAGE]: (state, action) => {
    const nextSessionKey = action.reviewFormSessionKey ?? state.reviewFormSessionKey
    const sessionChanged = !!action.reviewFormSessionKey &&
      action.reviewFormSessionKey !== state.reviewFormSessionKey

    return {
      reviewStage: action.reviewStage,
      reviewFormSessionKey: nextSessionKey,
      ...(sessionChanged ? {
        fetchingReviewCreate: false,
        reviewCreateError: undefined,
      } : {}),
    }
  },
})
